import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "../components/ui/button";

import { api } from "../services/api";
import AbsenceReasonsFilters from "../components/absence/AbsenceReasonsFilters";
import AbsenceReasonsTable from "../components/absence/AbsenceReasonsTable";
import AbsenceReasonFormDialog from "../components/absence/AbsenceReasonFormDialog";
import AbsenceReasonDeleteDialog from "../components/absence/AbsenceReasonDeleteDialog";

const AbsenceReasons = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [filterCategory, setFilterCategory] = useState("all");
  const [filterPaid, setFilterPaid] = useState("all");
  const [filterActive, setFilterActive] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    color: "#3B82F6",
    icon: "",
    is_paid: true,
    payment_rate: 100,
    deducts_from_salary: false,
    category: "OTHER",
    is_justified: true,
    is_planned: true,
    unit: "DAY",
    requires_approval: true,
    requires_justification: false,
    justification_mandatory: false,
    advance_notice_days: 0,
    max_duration_without_document: null,
    blocks_attendance: true,
    has_balance: false,
    default_annual_balance: null,
    allow_negative_balance: false,
    balance_renewal_period: "YEARLY",
    carry_over_unused_balance: false,
    max_carry_over_days: null,
    restricted_to_gender: null,
    restricted_to_roles: null,
    min_seniority_months: 0,
    is_active: true,
    display_order: 0,
  });

  const categories = [
    { value: "VACATION", label: t("absenceReasons.cat.VACATION"), icon: "🏖️" },
    { value: "SICK", label: t("absenceReasons.cat.SICK"), icon: "🏥" },
    { value: "UNPAID", label: t("absenceReasons.cat.UNPAID"), icon: "💰" },
    { value: "MATERNITY", label: t("absenceReasons.cat.MATERNITY"), icon: "👶" },
    { value: "PATERNITY", label: t("absenceReasons.cat.PATERNITY"), icon: "👨‍👦" },
    { value: "TRAINING", label: t("absenceReasons.cat.TRAINING"), icon: "📚" },
    { value: "COMPENSATION", label: t("absenceReasons.cat.COMPENSATION"), icon: "⏰" },
    { value: "ADMINISTRATIVE", label: t("absenceReasons.cat.ADMINISTRATIVE"), icon: "📄" },
    { value: "OTHER", label: t("absenceReasons.cat.OTHER"), icon: "📌" },
  ];

  const absenceReasonsQuery = useQuery({
    queryKey: ["absence-reasons"],
    queryFn: () => api.get("v1/absence-reasons").then((r) => r.data.items || []),
  });

  useEffect(() => {
    if (absenceReasonsQuery.isError) toast.error(t("absenceReasons.t.loadError"));
  }, [absenceReasonsQuery.isError, t]);

  const absenceReasons = absenceReasonsQuery.data ?? [];
  const loading = absenceReasonsQuery.isPending;

  const filteredReasons = useMemo(() => {
    let filtered = absenceReasons;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.code.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q))
      );
    }
    if (filterCategory && filterCategory !== "all") filtered = filtered.filter((r) => r.category === filterCategory);
    if (filterPaid && filterPaid !== "all") filtered = filtered.filter((r) => r.is_paid === (filterPaid === "true"));
    if (filterActive && filterActive !== "all") filtered = filtered.filter((r) => r.is_active === (filterActive === "true"));
    return filtered;
  }, [absenceReasons, searchTerm, filterCategory, filterPaid, filterActive]);

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      description: "",
      color: "#3B82F6",
      icon: "",
      is_paid: true,
      payment_rate: 100,
      deducts_from_salary: false,
      category: "OTHER",
      is_justified: true,
      is_planned: true,
      unit: "DAY",
      requires_approval: true,
      requires_justification: false,
      justification_mandatory: false,
      advance_notice_days: 0,
      max_duration_without_document: null,
      blocks_attendance: true,
      has_balance: false,
      default_annual_balance: null,
      allow_negative_balance: false,
      balance_renewal_period: "YEARLY",
      carry_over_unused_balance: false,
      max_carry_over_days: null,
      restricted_to_gender: null,
      restricted_to_roles: null,
      min_seniority_months: 0,
      is_active: true,
      display_order: 0,
    });
  };

  const handleAdd = () => {
    resetForm();
    setIsEditing(false);
    setSelectedReason(null);
    setShowModal(true);
  };

  const handleEdit = (reason) => {
    setFormData({
      ...reason,
      max_duration_without_document: reason.max_duration_without_document || null,
      default_annual_balance: reason.default_annual_balance || null,
      max_carry_over_days: reason.max_carry_over_days || null,
    });
    setIsEditing(true);
    setSelectedReason(reason);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast.error(t("absenceReasons.t.nameCodeRequired"));
      return;
    }

    if (formData.is_paid && formData.payment_rate <= 0) {
      toast.error(t("absenceReasons.t.payRatePositive"));
      return;
    }

    if (formData.has_balance && !formData.default_annual_balance) {
      toast.error(t("absenceReasons.t.annualBalanceRequired"));
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        max_duration_without_document: formData.max_duration_without_document || undefined,
        default_annual_balance: formData.default_annual_balance || undefined,
        max_carry_over_days: formData.max_carry_over_days || undefined,
      };

      if (isEditing && selectedReason) {
        await api.put(`v1/absence-reasons/${selectedReason.id}`, dataToSend);
        toast.success(t("absenceReasons.t.updated"));
      } else {
        await api.post("v1/absence-reasons", dataToSend);
        toast.success(t("absenceReasons.t.created"));
      }

      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ["absence-reasons"] });
    } catch (error) {
      toast.error(error.response?.data?.detail || t("absenceReasons.t.saveError"));
    }
  };

  const handleToggleStatus = async (reason) => {
    try {
      await api.post(`v1/absence-reasons/${reason.id}/toggle-status`);
      toast.success(reason.is_active ? t("absenceReasons.t.deactivated") : t("absenceReasons.t.activated"));
      queryClient.invalidateQueries({ queryKey: ["absence-reasons"] });
    } catch (error) {
      toast.error(t("absenceReasons.t.statusChangeError"));
    }
  };

  const handleDelete = async () => {
    if (!selectedReason) return;

    try {
      await api.delete(`v1/absence-reasons/${selectedReason.id}`);
      toast.success(t("absenceReasons.t.deleted"));
      setShowDeleteDialog(false);
      queryClient.invalidateQueries({ queryKey: ["absence-reasons"] });
    } catch (error) {
      toast.error(t("absenceReasons.t.deleteError"));
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("absenceReasons.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("absenceReasons.subtitle")}
          </p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4" />
          {t("absenceReasons.newReason")}
        </Button>
      </div>

      <AbsenceReasonsFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        filterPaid={filterPaid}
        setFilterPaid={setFilterPaid}
        filterActive={filterActive}
        setFilterActive={setFilterActive}
        categories={categories}
        filteredReasons={filteredReasons}
      />

      <AbsenceReasonsTable
        filteredReasons={filteredReasons}
        loading={loading}
        categories={categories}
        handleToggleStatus={handleToggleStatus}
        handleEdit={handleEdit}
        setSelectedReason={setSelectedReason}
        setShowDeleteDialog={setShowDeleteDialog}
      />

      <AbsenceReasonFormDialog
        showModal={showModal}
        setShowModal={setShowModal}
        isEditing={isEditing}
        formData={formData}
        updateField={updateField}
        categories={categories}
        handleSubmit={handleSubmit}
      />

      <AbsenceReasonDeleteDialog
        showDeleteDialog={showDeleteDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        selectedReason={selectedReason}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default AbsenceReasons;
